/**
 * Return-path helpers for the login round trip.
 *
 * The login routes honour `?redirect=/internal/path`: once the user is
 * authenticated they are sent to that path instead of Home. Every trip to a
 * login page (logout buttons, session expiry, route guards) should build its
 * target with `buildLoginPath` so the page the user was on travels along in
 * the URL. The URL param is the only mechanism on purpose: it survives a
 * refresh of the login page, is scoped to the tab, and needs no cleanup.
 *
 * This module must stay free of imports. `services/ApiCall.ts` imports it
 * statically and cannot pull in react-router or the store (circular deps).
 */

/**
 * Paths that are themselves part of signing in. They are never captured as a
 * return target and never accepted as one (a redirect back to `/login` after
 * login would loop). Deliberately a small, separate list: the public-route
 * lists in `ApiCall.ts` and `App.tsx` answer a different question and have
 * drifted from each other, so they are not reused here.
 */
const AUTH_ENTRY_PATHS = [
    "/login",
    "/owner-login",
    "/register",
    "/owner-register",
    "/forget-password",
    "/reset-password",
];

export interface ReturnLocation {
    pathname: string;
    search?: string;
    hash?: string;
}

export function isAuthEntryPath(pathname: string): boolean {
    return AUTH_ENTRY_PATHS.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`)
    );
}

const isSafeInternalPath = (raw: string | null): raw is string => {
    if (!raw || !raw.startsWith("/") || raw.startsWith("//")) {
        return false;
    }
    // Protocol-relative and scheme-carrying values never start with a single
    // "/", so the checks above already exclude open redirects; this only
    // keeps the login pages themselves out of the loop.
    const pathOnly = raw.split(/[?#]/)[0];
    return !isAuthEntryPath(pathOnly);
};

/**
 * Build the login URL that carries the current page as `?redirect=`.
 * Returns the bare login path when there is nothing worth returning to
 * (Home, or one of the auth-entry pages).
 */
export function buildLoginPath(
    location: ReturnLocation,
    options: { owner?: boolean } = {}
): string {
    const base = options.owner ? "/owner-login" : "/login";
    const pathname = location.pathname || "/";
    if (pathname === "/" || isAuthEntryPath(pathname)) {
        return base;
    }
    const current = `${pathname}${location.search ?? ""}${location.hash ?? ""}`;
    return `${base}?redirect=${encodeURIComponent(current)}`;
}

/**
 * Read `?redirect=` from a login page's query string. Only internal paths
 * are honoured; anything else (missing, external, protocol-relative, or an
 * auth-entry page) yields `fallback`.
 */
export function readSafeRedirect(search: string, fallback: string): string {
    const raw = new URLSearchParams(search).get("redirect");
    return isSafeInternalPath(raw) ? raw : fallback;
}
